import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  Bell,
  Sun,
  Moon, 
  User as UserIcon, 
  Settings, 
  LogOut, 
  ChevronDown, 
  Flag,
  Send,
  Mail, 
  Bug, 
  Lightbulb, 
  FileText, 
  BarChart3,
  X
} from "lucide-react";
import { useAuthContext } from "../hooks/useAuthContext";
import { useTheme } from "../context/ThemeContext";
import { useLogout } from "../hooks/useLogout";
import { useLang } from "../hooks/useLang";
import { useToast } from "../hooks/use-toast";
import LogoMark from "./LogoMark";

const Navbar = () => {
  const { user, token } = useAuthContext();
  const { theme, toggleTheme } = useTheme();
  const { logout } = useLogout();
  const { urlLanguage } = useLang();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportType, setReportType] = useState("bug");
  const [reportText, setReportText] = useState("");
  const [reportMethod, setReportMethod] = useState(null);

  const profileMenuRef = useRef(null);
  const notifRef = useRef(null);

  const fullName = `${user?.firstname || ''} ${user?.lastname || ''}`.trim() || user?.username || "User";
  const initials = (`${user?.firstname?.[0] || ''}${user?.lastname?.[0] || ''}`.toUpperCase()) || fullName.substring(0, 2).toUpperCase();

  useEffect(() => {
    const handler = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await fetch('/api/notifications', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      }
    } catch (err) {
      console.error('Failed to load notifications:', err);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const notificationLink = (n) => {
    if (n.type === 'follow') return n.related_id ? urlLanguage(`/${n.related_id}`) : null;
    if (n.type === 'new_upload' || n.type === 'rating') return n.related_id ? urlLanguage(`/material/${n.related_id}`) : null;
    return null;
  };

  const handleOpenNotifications = () => {
    setShowNotifications((prev) => !prev);
    if (!showNotifications) fetchNotifications();
  };

  const handleNotificationClick = async (n) => {
    setShowNotifications(false);
    const link = notificationLink(n);
    if (link) navigate(link);
    if (!n.is_read) {
      try {
        await fetch(`/api/notifications/${n.id}/read`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
        setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (err) {
        console.error('Failed to mark notification read:', err);
      }
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await fetch('/api/notifications/read-all', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      setNotifications((prev) => prev.map((x) => ({ ...x, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all notifications read:', err);
    }
  };

  const timeAgo = (dateStr) => {
    const diffMs = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const navLinks = [
    { label: "Dashboard", href: urlLanguage("/dashboard") },
    { label: "My Books", href: urlLanguage("/notes") },
    { label: "Library", href: urlLanguage("/library") },
  ];

  const profileMenuItems = [
    { label: "Profile", icon: UserIcon, href: user?.username ? `/${user.username}` : null },
    { label: "Analytics", icon: BarChart3, href: urlLanguage("/analytics") },
    { label: "Forms", icon: FileText, href: urlLanguage("/forms") },
    { label: "Settings", icon: Settings, href: urlLanguage("/settings") },
    { label: "Report a Problem", icon: Flag, href: null },
  ];

  const handleMenuClick = (item) => {
    setShowProfileMenu(false);
    if (item.href) {
      navigate(item.href);
    } else {
      setShowReportModal(true);
      setReportMethod(null);
      setReportText("");
      setReportType("bug");
    }
  };

  const handleLogout = () => {
    setShowProfileMenu(false);
    logout();
  };

  const handleSendReport = async () => {
    if (!reportText.trim()) return;
    
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/reports', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: reportType,
          description: reportText
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Report Submitted",
          description: data.message || "Thank you for your feedback!",
        });
        setShowReportModal(false);
        setReportText("");
      } else {
        toast({
          title: "Submission Failed",
          description: data.error || "Failed to submit report. Please try again.",
          variant: "destructive"
        });
      }
    } catch (err) {
      console.error("Error submitting report:", err);
      toast({
        title: "Error",
        description: "A network error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <nav className="bg-nav-bg sticky top-0 z-50 border-b border-border px-6 h-16 flex items-center justify-between">
        <div className="relative max-w-[1440px] mx-auto w-full flex items-center justify-between">
          <div className="flex items-center">
            <LogoMark to={urlLanguage('/')} iconHeight={44} textHeight={26} />
          </div>

          <div className="hidden lg:flex items-stretch gap-1 absolute left-1/2 -translate-x-1/2 h-16">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.href || (link.href === urlLanguage('/dashboard') && location.pathname === urlLanguage('/'));
              return (
                <Link
                  key={link.label}
                  to={link.href}
                  className={`group relative px-4 h-16 flex items-center text-sm transition-colors duration-200 no-underline ${
                    isActive
                      ? "text-foreground font-semibold"
                      : "text-nav-foreground/60 hover:text-foreground font-medium"
                  }`}
                >
                  {link.label}
                  {/* A hairline under the label carries "you are here" — no box,
                      no background wash. It grows from the centre on hover. */}
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none absolute bottom-0 left-4 right-4 h-[2px] rounded-full bg-primary origin-center transition-transform duration-300 ease-out ${
                      isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100 bg-primary/40"
                    }`}
                  />
                </Link>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="w-10 h-10 rounded-xl bg-nav-hover flex items-center justify-center text-nav-foreground/70 hover:text-foreground transition-all border-none cursor-pointer"
            >
              {theme === "light" ? <Moon size={20} /> : <Sun size={20} />}
            </button>

            <div className="relative" ref={notifRef}>
              <button
                onClick={handleOpenNotifications}
                className="relative w-10 h-10 rounded-xl bg-nav-hover flex items-center justify-center text-nav-foreground/70 hover:text-foreground transition-all border-none cursor-pointer"
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                <div className="absolute right-0 top-full mt-2 w-80 max-h-[28rem] bg-card rounded-2xl shadow-xl border border-border overflow-hidden animate-slide-down z-50 flex flex-col">
                  <div className="p-4 border-b border-border flex items-center justify-between flex-shrink-0">
                    <p className="text-sm font-bold text-foreground">Notifications</p>
                    {unreadCount > 0 && (
                      <button onClick={handleMarkAllRead} className="text-[11px] font-bold text-primary bg-transparent border-none cursor-pointer hover:underline">
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="overflow-y-auto">
                    {notifications.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-10">No notifications yet.</p>
                    ) : (
                      notifications.map((n) => (
                        <button
                          key={n.id}
                          onClick={() => handleNotificationClick(n)}
                          className={`w-full flex items-start gap-2 px-4 py-3 text-left border-none cursor-pointer transition-colors border-b border-border/50 last:border-b-0 ${n.is_read ? 'bg-transparent hover:bg-muted' : 'bg-primary/5 hover:bg-primary/10'}`}
                        >
                          {!n.is_read && <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />}
                          <div className={`min-w-0 ${n.is_read ? 'ml-3.5' : ''}`}>
                            <p className="text-xs text-foreground leading-snug">{n.message}</p>
                            <p className="text-[10px] text-muted-foreground mt-1">{timeAgo(n.createdAt)}</p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="relative" ref={profileMenuRef}>
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-xl bg-nav-hover hover:bg-nav-hover/80 transition-all border-none cursor-pointer"
              >
                <div className="w-8 h-8 rounded-lg bg-secondary text-secondary-foreground flex items-center justify-center font-bold text-xs overflow-hidden flex-shrink-0">
                  {user?.avatar_url ? (
                    <img src={user.avatar_url} alt={fullName} className="w-full h-full object-cover" />
                  ) : (
                    initials
                  )}
                </div>
                <span className="text-sm font-medium text-foreground hidden sm:block">
                  {user?.firstname || fullName}
                </span>
                <ChevronDown className={`w-3.5 h-3.5 text-nav-foreground/50 transition-transform ${showProfileMenu ? "rotate-180" : ""}`} />
              </button>

              {showProfileMenu && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-card rounded-2xl shadow-xl border border-border overflow-hidden animate-slide-down z-50">
                  <div className="p-4 border-b border-border bg-muted/30">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center text-secondary-foreground font-black text-sm overflow-hidden flex-shrink-0">
                        {user?.avatar_url ? (
                          <img src={user.avatar_url} alt={fullName} className="w-full h-full object-cover" />
                        ) : (
                          initials
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-foreground truncate">{fullName}</p>
                        <p className="text-[10px] text-muted-foreground font-medium truncate">@{user?.username}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="py-1">
                    {profileMenuItems.map((item) => (
                      <button
                        key={item.label}
                        onClick={() => handleMenuClick(item)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-all border-none cursor-pointer text-left bg-transparent"
                      >
                        <item.icon className="w-4 h-4 text-muted-foreground" />
                        {item.label}
                      </button>
                    ))}
                  </div>

                  <div className="border-t border-border py-1">
                    <button 
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-bold text-destructive hover:bg-destructive/10 transition-all border-none cursor-pointer text-left bg-transparent"
                    >
                      <LogOut className="w-4 h-4" />
                      Log Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-background/40 backdrop-blur-sm" onClick={() => setShowReportModal(false)} />
          <div className="relative bg-card rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-in">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h3 className="section-title flex items-center gap-2">
                <Flag className="w-4 h-4 text-secondary" /> Report a Problem
              </h3>
              <button onClick={() => setShowReportModal(false)} className="w-8 h-8 rounded-xl hover:bg-muted flex items-center justify-center transition-colors border-none cursor-pointer bg-transparent">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {!reportMethod ? (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">How would you like to report?</p>
                  <button
                    onClick={() => setReportMethod("send")}
                    className="w-full flex items-center gap-3 p-4 rounded-xl border border-border hover:border-secondary hover:bg-secondary/5 transition-all text-left bg-transparent cursor-pointer"
                  >
                    <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                      <Send className="w-5 h-5 text-secondary" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground">Send Now</p>
                      <p className="text-[10px] text-muted-foreground">Report directly from here — we'll review it shortly</p>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                        const subject = encodeURIComponent(`[${reportType}] Report from YeniDers`);
                        window.open(`mailto:support@yeniders.com?subject=${subject}`, "_blank");
                        setShowReportModal(false);
                    }}
                    className="w-full flex items-center gap-3 p-4 rounded-xl border border-border hover:border-primary hover:bg-primary/5 transition-all text-left bg-transparent cursor-pointer"
                  >
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground">Send via Email</p>
                      <p className="text-[10px] text-muted-foreground">Opens your email client to support@yeniders.com</p>
                    </div>
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-bold text-foreground mb-2 block uppercase tracking-wider">Type</label>
                    <div className="flex gap-2">
                      {["bug", "suggestion", "other"].map((id) => (
                        <button
                          key={id}
                          onClick={() => setReportType(id)}
                          className={`flex-1 flex flex-col items-center gap-1.5 p-3 rounded-xl text-xs font-bold transition-all border cursor-pointer ${
                            reportType === id
                              ? "bg-secondary/15 text-secondary border-secondary/30"
                              : "bg-muted text-muted-foreground border-transparent hover:text-foreground hover:bg-muted/80"
                          }`}
                        >
                          {id === 'bug' && <Bug size={16} />}
                          {id === 'suggestion' && <Lightbulb size={16} />}
                          {id === 'other' && <Flag size={16} />}
                          <span className="capitalize">{id}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-foreground mb-1.5 block uppercase tracking-wider">Description</label>
                    <textarea
                      value={reportText}
                      onChange={(e) => setReportText(e.target.value)}
                      placeholder="Describe the issue, suggestion, or feedback..."
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl bg-muted border-0 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none font-sans"
                    />
                  </div>

                  <div className="flex gap-2">
                    <button onClick={() => setReportMethod(null)}
                      className="flex-1 h-10 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:bg-muted transition-all bg-transparent cursor-pointer">
                      Back
                    </button>
                    <button
                      onClick={handleSendReport}
                      disabled={!reportText.trim() || isSubmitting}
                      className="flex-1 h-10 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-1.5 border-none cursor-pointer"
                    >
                      {isSubmitting ? (
                        <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                      ) : (
                        <><Send className="w-4 h-4" /> Submit</>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
